import React, { useEffect, useRef, useState } from "react";
import { getRecaptchaToken } from "../../services/api/recaptcha";
import { track } from "../../services/analytics";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.axoix.in";
const NAME_STORAGE_KEY = "axoix_comment_name";
const EMAIL_STORAGE_KEY = "axoix_comment_email";

interface BlogCommentsProps {
  slug: string;
  allowComments: boolean;
  commentCount: number;
}

interface ApiComment {
  id: number;
  author_name: string;
  body: string;
  created_at: string | null;
  parent_id: number | null;
}

type SubmitStatus = "idle" | "submitting" | "success" | "error";

const styles = `
  .blog-comments { margin-top: 60px; }
  .blog-comments-heading { margin-bottom: 30px; }
  .blog-comment-item { padding: 20px; border-radius: 12px; margin-bottom: 16px; }
  .blog-comment-item.is-reply { margin-left: 40px; }
  .blog-comment-author { font-weight: 700; margin-right: 10px; }
  .blog-comment-date { font-size: 12px; opacity: 0.5; }
  .blog-comment-body { margin: 10px 0 12px; opacity: 0.85; line-height: 1.7; white-space: pre-wrap; }
  .blog-comment-reply-btn {
    background: none; border: none; padding: 0; cursor: pointer;
    color: var(--color-primary); font-size: 13px; font-weight: 600;
  }
  .blog-comment-form { margin-top: 30px; max-width: 640px; }
  .blog-comment-form .row-fields { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
  .blog-comment-form input, .blog-comment-form textarea {
    width: 100%;
    padding: 14px 18px;
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 10px;
    color: var(--color-heading, #fff);
    background: rgba(255,255,255,0.04);
    outline: none;
  }
  .blog-comment-form input:focus, .blog-comment-form textarea:focus { border-color: var(--color-primary); }
  .blog-comment-form .field { flex: 1; min-width: 200px; }
  .blog-comment-form textarea { min-height: 110px; resize: vertical; }
  .blog-comment-form button {
    margin-top: 6px;
    height: 50px;
    padding: 0 30px;
    border: none;
    border-radius: 100px;
    font-weight: 700;
    font-size: 14px;
    color: var(--color-secondary, #00020f);
    background: var(--color-primary);
    cursor: pointer;
  }
  .blog-comment-form button:disabled { opacity: 0.6; cursor: not-allowed; }
  .blog-comment-replying-to {
    display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
    font-size: 13px; opacity: 0.75;
  }
  .blog-comment-replying-to button { background: none; border: none; color: var(--color-primary); cursor: pointer; padding: 0; }
  .blog-comments-message { margin-top: 16px; font-size: 14px; }
  .blog-comments-message.success { color: var(--color-primary); }
  .blog-comments-message.error { color: #ff6b6b; }
  .blog-comment-honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; overflow: hidden; }
`;

const formatRelativeDate = (isoDate: string | null): string => {
  if (!isoDate) return "";
  const then = new Date(isoDate).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(isoDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const BlogComments: React.FC<BlogCommentsProps> = ({ slug, allowComments, commentCount }) => {
  const [comments, setComments] = useState<ApiComment[]>([]);
  const [total, setTotal] = useState(commentCount);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [replyingTo, setReplyingTo] = useState<{ id: number; author: string } | null>(null);
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setName(localStorage.getItem(NAME_STORAGE_KEY) || "");
    setEmail(localStorage.getItem(EMAIL_STORAGE_KEY) || "");
  }, []);

  const fetchComments = () => {
    fetch(`${API_BASE}/api/chorus/blog/posts/by-slug/${slug}/comments?per_page=50`)
      .then((res) => res.json())
      .then((data) => {
        setComments(data.comments || []);
        setTotal(typeof data.total === "number" ? data.total : commentCount);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    if (!allowComments) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchComments();
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, allowComments]);

  if (!allowComments) return null;

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !body.trim()) return;

    setStatus("submitting");
    setMessage("");

    try {
      const recaptcha_token = website.trim() === "" ? await getRecaptchaToken("comment_submit") : "";
      const response = await fetch(`${API_BASE}/api/chorus/blog/posts/by-slug/${slug}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          author_name: name.trim(),
          author_email: email.trim(),
          body: body.trim(),
          parent_id: replyingTo?.id,
          website,
          recaptcha_token,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to submit comment.");

      localStorage.setItem(NAME_STORAGE_KEY, name.trim());
      localStorage.setItem(EMAIL_STORAGE_KEY, email.trim());
      setStatus("success");
      setMessage(data.message || "Thanks — your comment is awaiting moderation.");
      setBody("");
      setReplyingTo(null);
      track("comment_submit");
    } catch (err: unknown) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  };

  const topLevel = comments.filter((c) => c.parent_id === null);
  const repliesFor = (id: number) => comments.filter((c) => c.parent_id === id);

  const renderComment = (comment: ApiComment, isReply: boolean) => (
    <div key={comment.id} className={`blog-comment-item xb-border${isReply ? " is-reply" : ""}`}>
      <span className="blog-comment-author">{comment.author_name}</span>
      <span className="blog-comment-date">{formatRelativeDate(comment.created_at)}</span>
      <p className="blog-comment-body">{comment.body}</p>
      {!isReply && (
        <button
          type="button"
          className="blog-comment-reply-btn"
          onClick={() => setReplyingTo({ id: comment.id, author: comment.author_name })}
        >
          Reply
        </button>
      )}
    </div>
  );

  return (
    <div className="blog-comments" ref={containerRef}>
      <style>{styles}</style>
      <h3 className="blog-comments-heading">
        {total > 0 ? `${total} Comment${total === 1 ? "" : "s"}` : "Comments"}
      </h3>

      {loaded && topLevel.length === 0 && (
        <p style={{ opacity: 0.6, fontSize: 14 }}>Be the first to comment.</p>
      )}

      {topLevel.map((comment) => (
        <React.Fragment key={comment.id}>
          {renderComment(comment, false)}
          {repliesFor(comment.id).map((reply) => renderComment(reply, true))}
        </React.Fragment>
      ))}

      <form className="blog-comment-form" onSubmit={submitHandler}>
        {replyingTo && (
          <div className="blog-comment-replying-to">
            Replying to <strong>{replyingTo.author}</strong>
            <button type="button" onClick={() => setReplyingTo(null)}>Cancel</button>
          </div>
        )}

        <div className="row-fields">
          <div className="field">
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <input
              type="email"
              placeholder="Your email (not published)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <textarea
            placeholder="Write a comment..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
          />
        </div>

        <input
          type="text"
          name="website"
          className="blog-comment-honeypot"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        <button type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Posting..." : "Post Comment"}
        </button>

        {status === "success" && <p className="blog-comments-message success">{message}</p>}
        {status === "error" && <p className="blog-comments-message error">{message}</p>}
      </form>
    </div>
  );
};

export default BlogComments;
